import { ApiService } from '@quorum/api';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AppointmentCase,
  Media,
  PartSalesDetail,
  Service,
  ServiceGroup,
  ServiceGroupService,
  Vehicle,
  ServiceAppointment,
  Employee,
} from '@quorum/models/xs-resource';
import { MediaStateService } from '@quorum/sha-media';
import { ServiceGroupSelectionComponent } from '../service-group-selection/service-group-selection.component';
import { MatDialog } from '@angular/material/dialog';
import { AppointmentCaseCreateEditDialogComponent } from '../appointment-case-create-edit-dialog/appointment-case-create-edit-dialog.component';
import { ShowLineItemServicesDialogComponent } from '../show-line-item-services-dialog/show-line-item-services-dialog.component';
import moment from 'moment';
@Component({
  selector: 'sha-appointment-case-list',
  templateUrl: './appointment-case-list.component.html',
  styleUrls: ['./appointment-case-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentCaseListComponent implements OnInit {
  @Input() cases$: Observable<AppointmentCase[]>;
  @Input() partSalesDetails$: Observable<PartSalesDetail[]>;
  @Input() serviceGroups$: Observable<ServiceGroup[]>;
  @Input() authenticatedEmployee: Employee;
  @Input() dropdowns$: Observable<any>;
  @Input() appointment$: Observable<ServiceAppointment>;
  @Input() vehicle$: Observable<Vehicle>;
  @Input() gmServiceFromTree: any;

  @Output() editCase: EventEmitter<AppointmentCase> = new EventEmitter();
  @Output() caseDeleted: EventEmitter<AppointmentCase> = new EventEmitter();
  @Output() caseListView: EventEmitter<string> = new EventEmitter();
  @Output() serviceGroupSelected: EventEmitter<ServiceGroup> = new EventEmitter();
  @Output() createCase: EventEmitter<any> = new EventEmitter();

  casesWithGroups$: Observable<any>;
  caseToEdit: AppointmentCase = null;
  mediaParams$: Observable<any>;
  media$: Observable<Media[]>;
  casesWithGroupsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private apiService: ApiService,
    public serviceLaneFacade: ServiceLaneFacade,
    public mediaStateService: MediaStateService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.mediaParams$ = this.mediaStateService.getMediaParamsForKey('appointmentCase');
    this.casesWithGroups$ = combineLatest([this.cases$, this.partSalesDetails$]).pipe(
      mergeMap(([appointmentCases, partSalesDetails]) => {
        const serviceGroupIds = appointmentCases.map((appointmentCase) => appointmentCase.serviceGroupId);
        return this.serviceLaneFacade.serviceGroups$.pipe(
          filter((serviceGroups) => serviceGroups.length > 0),
          map((serviceGroups) => serviceGroups.filter((serviceGroup) => serviceGroupIds.includes(serviceGroup.id))),
          map((serviceGroups: any) => serviceGroups.map((serviceGroup: any) => serviceGroup.services).flat()),
          switchMap((serviceGroupServices: ServiceGroupService[]) => {
            return this.apiService
              .get(`v/1/fixed-operations/services`, {
                params: { id: serviceGroupServices.map((service: any) => service.serviceId).join(',') },
              })
              .pipe(
                map((d) => {
                  const casesWithGroups: any[] = [];
                  const today = new Date();
                  appointmentCases.forEach((appointmentCase) => {
                    let reservedParts = false;
                    if (partSalesDetails.length > 0) {
                      reservedParts = partSalesDetails.some(
                        (partSalesDetail: PartSalesDetail) => partSalesDetail.warrantyCase === appointmentCase.sequence
                      );
                    }
                    let rate: number = null;
                    const thisServices = serviceGroupServices
                      .filter((service: any) => {
                        return service.serviceGroupId === appointmentCase.serviceGroupId;
                      })
                      .map((serviceGroupService: any) =>
                        d.find((item: any) => item.id === serviceGroupService.serviceId)
                      );
                    this.serviceLaneFacade
                      .selectServiceGroup(appointmentCase.serviceGroupId)
                      .pipe(take(1))
                      .subscribe((serviceGroup) => {
                        if (
                          serviceGroup &&
                          serviceGroup.serviceGroupRate &&
                          new Date(serviceGroup.serviceGroupRate.effectiveDate) < today &&
                          new Date(serviceGroup.serviceGroupRate.expiryDate) > today
                        ) {
                          rate = serviceGroup.serviceGroupRate.rate;
                        }
                      });
                    casesWithGroups.push({
                      case: appointmentCase,
                      services: thisServices,
                      rate,
                      reservedParts,
                    });
                  });
                  this.casesWithGroupsLoading$.next(false);
                  return casesWithGroups;
                })
              );
          }),
          take(1)
        );
      })
    );
  }

  editAppointmentCase(
    serviceGroups: ServiceGroup[],
    authenticatedEmployee: Employee,
    vehicle: Vehicle,
    appointment: ServiceAppointment,
    dropdowns: any,
    gmServiceFromTree: any,
    caseToEdit: AppointmentCase
  ) {
    const dialogRef = this.dialog.open(AppointmentCaseCreateEditDialogComponent, {
      width: '90%',
      maxWidth: '681px',
      height: '60vh',
      maxHeight: '60vh',
      minHeight: '736px',
      disableClose: true,
      data: { serviceGroups, authenticatedEmployee, vehicle, appointment, dropdowns, gmServiceFromTree, caseToEdit },
    });

    // disable scrolling of background on tablet views
    document.body.classList.add('cdk-global-scrollblock');

    dialogRef.afterClosed().subscribe((result: { appointmentCase: AppointmentCase; addManually: boolean }) => {
      // enable scrolling again once dialog is closed
      document.body.classList.remove('cdk-global-scrollblock');

      if (result && result.appointmentCase) {
        this.editCase.emit({
          ...result.appointmentCase,
          startTime: result.appointmentCase.startTime
            ? moment(result.appointmentCase.startTime, 'hh:mm').toDate()
            : null,
        });
      }
    });
  }

  deleteCase(caseToDelete: AppointmentCase) {
    this.caseDeleted.emit(caseToDelete);
  }

  convertStringCase(str: string) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  setCaseListView(caseListView: string) {
    this.caseListView.emit(caseListView);
  }

  serviceGroupLookup(
    serviceGroups: ServiceGroup[],
    authenticatedEmployee: Employee,
    vehicle: Vehicle,
    appointment: ServiceAppointment,
    dropdowns: any,
    gmServiceFromTree: any
  ) {
    const dialogRef = this.dialog.open(ServiceGroupSelectionComponent, {
      width: '90%',
      maxWidth: '681px',
      height: '60vh',
      maxHeight: '60vh',
      minHeight: '736px',
      disableClose: true,
      data: { serviceGroups: serviceGroups, authenticatedEmployee: authenticatedEmployee },
    });

    // disable scrolling of background on tablet views
    document.body.classList.add('cdk-global-scrollblock');

    dialogRef.afterClosed().subscribe((result: { selectedServiceGroup: ServiceGroup; addManually: boolean }) => {
      // enable scrolling again once dialog is closed
      document.body.classList.remove('cdk-global-scrollblock');

      if (result) {
        if (result.selectedServiceGroup) {
          const selectedServiceGroup = result.selectedServiceGroup;
          this.serviceGroupSelected.emit(selectedServiceGroup);
        } else if (result.addManually) {
          this.addCaseManually(
            serviceGroups,
            authenticatedEmployee,
            vehicle,
            appointment,
            dropdowns,
            gmServiceFromTree
          );
        }
      }
    });
  }

  addCaseManually(
    serviceGroups: ServiceGroup[],
    authenticatedEmployee: Employee,
    vehicle: Vehicle,
    appointment: ServiceAppointment,
    dropdowns: any,
    gmServiceFromTree: any
  ) {
    const dialogRef = this.dialog.open(AppointmentCaseCreateEditDialogComponent, {
      width: '90%',
      maxWidth: '681px',
      height: '60vh',
      maxHeight: '60vh',
      minHeight: '736px',
      disableClose: true,
      data: { serviceGroups, authenticatedEmployee, vehicle, appointment, dropdowns, gmServiceFromTree },
    });

    // disable scrolling of background on tablet views
    document.body.classList.add('cdk-global-scrollblock');

    dialogRef.afterClosed().subscribe((result: { appointmentCase: AppointmentCase; addManually: boolean }) => {
      // enable scrolling again once dialog is closed
      document.body.classList.remove('cdk-global-scrollblock');

      if (result) {
        if (result.appointmentCase) {
          this.createCase.emit({
            ...result.appointmentCase,
            startTime: result.appointmentCase.startTime
              ? moment(result.appointmentCase.startTime, 'hh:mm').toDate()
              : null,
          });
        } else if (!result.addManually) {
          this.serviceGroupLookup(
            serviceGroups,
            authenticatedEmployee,
            vehicle,
            appointment,
            dropdowns,
            gmServiceFromTree
          );
        }
      }
    });
  }

  showServices(currentCase: AppointmentCase, services: Service[]) {
    this.dialog.open(ShowLineItemServicesDialogComponent, {
      width: '90%',
      maxHeight: '70vh',
      disableClose: true,
      data: { case: currentCase, services: services },
    });
  }
}
